// Meta (Facebook) Pixel Integration
// Pixel ID is now configurable via Admin Panel Settings

// Track if pixel is already initialized to prevent duplicates
let pixelInitialized = false;
let pixelScriptElement: HTMLScriptElement | null = null;
let fbqReady = false;
let fbqReadyCallbacks: (() => void)[] = [];

// Wait for fbq to be ready
function onFbqReady(callback: () => void) {
  if (fbqReady && window.fbq) {
    callback();
  } else {
    fbqReadyCallbacks.push(callback);
  }
}

// Mark fbq as ready and run callbacks
function setFbqReady() {
  fbqReady = true;
  fbqReadyCallbacks.forEach(cb => cb());
  fbqReadyCallbacks = [];
}

function getPixelId(): string {
  if (typeof window === 'undefined') return '';
  // Check if pixel is enabled
  const enabled = localStorage.getItem('meta_pixel_enabled') === 'true';
  if (!enabled) return '';
  // Get pixel ID from localStorage (set via admin panel)
  return localStorage.getItem('meta_pixel_id') || '';
}

function isPixelEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('meta_pixel_enabled') === 'true' && !!localStorage.getItem('meta_pixel_id');
}

interface PixelEvent {
  eventName: string;
  params?: Record<string, unknown>;
}

// Initialize Meta Pixel
declare global {
  interface Window {
    fbq: (command: string, event: string, params?: Record<string, unknown>) => void;
    _fbq?: unknown;
  }
}

export function initMetaPixel() {
  if (typeof window === 'undefined') return;
  
  const PIXEL_ID = getPixelId();
  
  // If pixel was previously initialized but now disabled/removed, clean up
  if (pixelInitialized && !PIXEL_ID) {
    console.log('[Meta Pixel] Disabled - cleaning up existing pixel');
    disableMetaPixel();
    return;
  }
  
  if (!PIXEL_ID) {
    console.log('[Meta Pixel] Not initialized - no Pixel ID configured or tracking disabled');
    return;
  }
  
  // Prevent duplicate initialization
  if (pixelInitialized) {
    console.log('[Meta Pixel] Already initialized, skipping duplicate');
    return;
  }
  
  // Inject Pixel base code
  const script = document.createElement('script');
  script.async = true;
  script.id = 'meta-pixel-script';
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${PIXEL_ID}');
    fbq('track', 'PageView');
    // Notify that fbq is ready
    if (window.fbq) {
      window.fbq('trackCustom', 'PixelReady', {});
    }
  `;
  document.head.appendChild(script);
  pixelScriptElement = script;
  pixelInitialized = true;
  
  // Poll for fbq availability (Facebook script loads async)
  const checkFbqReady = () => {
    if (typeof window !== 'undefined' && window.fbq && typeof window.fbq === 'function') {
      setFbqReady();
      console.log('[Meta Pixel] fbq is ready and functional');
    } else {
      setTimeout(checkFbqReady, 100);
    }
  };
  
  // Start polling after a short delay to let script begin loading
  setTimeout(checkFbqReady, 200);
  
  console.log('[Meta Pixel] Initialized with ID:', PIXEL_ID);
}

// Disable/remove Meta Pixel
export function disableMetaPixel() {
  if (typeof window === 'undefined') return;
  
  // Remove the script element
  if (pixelScriptElement && pixelScriptElement.parentNode) {
    pixelScriptElement.parentNode.removeChild(pixelScriptElement);
    pixelScriptElement = null;
  }
  
  // Remove any dynamically added fbevents scripts
  const existingScripts = document.querySelectorAll('script[src*="fbevents.js"]');
  existingScripts.forEach(s => s.remove());
  
  // Clear fbq from window (optional - prevents further tracking)
  if (window.fbq) {
    // @ts-expect-error - deleting global
    delete window.fbq;
  }
  if (window._fbq) {
    // @ts-expect-error - deleting global
    delete window._fbq;
  }
  
  pixelInitialized = false;
  console.log('[Meta Pixel] Disabled and cleaned up');
}

// Re-initialize pixel (call after settings change)
export function reinitMetaPixel() {
  if (typeof window === 'undefined') return;
  
  console.log('[Meta Pixel] Re-initializing...');
  
  // First disable existing pixel
  disableMetaPixel();
  
  // Small delay to ensure cleanup completes
  setTimeout(() => {
    // Then re-initialize with new settings
    initMetaPixel();
  }, 100);
}

// Track custom events
export function trackPixelEvent(eventName: string, params?: Record<string, unknown>): Promise<boolean> {
  return new Promise((resolve) => {
    // Check if pixel is enabled and initialized
    if (!isPixelEnabled()) {
      console.log('[Meta Pixel] Event not tracked - pixel disabled:', eventName);
      resolve(false);
      return;
    }
    
    // Try to track immediately if fbq is ready
    const tryTrack = () => {
      if (typeof window !== 'undefined' && window.fbq && typeof window.fbq === 'function') {
        window.fbq('track', eventName, params);
        console.log('[Meta Pixel] Event tracked:', eventName, params);
        resolve(true);
      } else {
        console.log('[Meta Pixel] fbq not ready yet, will retry...');
        resolve(false);
      }
    };
    
    // If fbq is already ready, track immediately
    if (fbqReady && window.fbq) {
      tryTrack();
      return;
    }
    
    // Otherwise wait for it to be ready (max 5 seconds)
    let attempts = 0;
    const maxAttempts = 50; // 50 * 100ms = 5 seconds
    
    const waitAndRetry = () => {
      attempts++;
      if (typeof window !== 'undefined' && window.fbq && typeof window.fbq === 'function') {
        window.fbq('track', eventName, params);
        console.log('[Meta Pixel] Event tracked after waiting:', eventName, params);
        setFbqReady();
        resolve(true);
      } else if (attempts < maxAttempts) {
        setTimeout(waitAndRetry, 100);
      } else {
        console.log('[Meta Pixel] Event not tracked - fbq not available after waiting:', eventName);
        resolve(false);
      }
    };
    
    waitAndRetry();
  });
}

// Check if Meta Pixel is active
export function isMetaPixelActive(): boolean {
  if (typeof window === 'undefined') return false;
  return isPixelEnabled() && pixelInitialized && fbqReady && typeof window.fbq === 'function';
}

// Check if pixel is initialized (but fbq might still be loading)
export function isPixelInitialized(): boolean {
  if (typeof window === 'undefined') return false;
  return isPixelEnabled() && pixelInitialized;
}

// Standard Meta Events
export const MetaEvents = {
  // PageView - automatically tracked on init
  
  // ViewContent - when user views a product
  viewContent: (contentName: string, contentType: string, contentIds: string[], value?: number, currency = 'INR') => {
    trackPixelEvent('ViewContent', {
      content_name: contentName,
      content_type: contentType,
      content_ids: contentIds,
      value,
      currency,
    });
  },
  
  // AddToCart - when user adds to cart
  addToCart: (contentName: string, contentType: string, contentIds: string[], value: number, currency = 'INR') => {
    trackPixelEvent('AddToCart', {
      content_name: contentName,
      content_type: contentType,
      content_ids: contentIds,
      value,
      currency,
    });
  },
  
  // InitiateCheckout - when user starts checkout
  initiateCheckout: (contentIds: string[], value: number, currency = 'INR') => {
    trackPixelEvent('InitiateCheckout', {
      content_ids: contentIds,
      value,
      currency,
    });
  },
  
  // Purchase - when order is placed
  purchase: (contentIds: string[], value: number, currency = 'INR', orderId?: string) => {
    trackPixelEvent('Purchase', {
      content_ids: contentIds,
      value,
      currency,
      order_id: orderId,
    });
  },
  
  // Lead - when user submits contact form
  lead: (contentName?: string) => {
    trackPixelEvent('Lead', {
      content_name: contentName || 'Contact Form',
    });
  },
  
  // CompleteRegistration - when user signs up
  completeRegistration: (contentName?: string) => {
    trackPixelEvent('CompleteRegistration', {
      content_name: contentName || 'User Registration',
    });
  },
  
  // Search - when user searches
  search: (searchString: string) => {
    trackPixelEvent('Search', {
      search_string: searchString,
    });
  },
  
  // AddToWishlist - when user favorites a product
  addToWishlist: (contentName: string, contentType: string, contentIds: string[], value?: number, currency = 'INR') => {
    trackPixelEvent('AddToWishlist', {
      content_name: contentName,
      content_type: contentType,
      content_ids: contentIds,
      value,
      currency,
    });
  },
};

// UTM Parameter tracking for campaign attribution
export function getUTMParams() {
  if (typeof window === 'undefined') return {};
  
  const urlParams = new URLSearchParams(window.location.search);
  return {
    utm_source: urlParams.get('utm_source') || '',
    utm_medium: urlParams.get('utm_medium') || '',
    utm_campaign: urlParams.get('utm_campaign') || '',
    utm_content: urlParams.get('utm_content') || '',
    utm_term: urlParams.get('utm_term') || '',
    fbclid: urlParams.get('fbclid') || '', // Facebook click ID
  };
}

// Store UTM params in session for attribution
export function storeUTMParams() {
  const params = getUTMParams();
  if (Object.values(params).some(v => v)) {
    sessionStorage.setItem('utm_attribution', JSON.stringify(params));
  }
}

// Get stored UTM params
export function getStoredUTMParams() {
  try {
    const stored = sessionStorage.getItem('utm_attribution');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// Track with UTM attribution
export function trackWithAttribution(eventName: string, params: Record<string, unknown> = {}) {
  const utmParams = getStoredUTMParams();
  trackPixelEvent(eventName, {
    ...params,
    ...utmParams,
  });
}